import { useEffect, useState } from 'react';
import InputField from '../../../Forms/Input/InputField';
import SelectField from '../../../Forms/Input/SelectField';
import { useMultiplePayment } from '../../../../hooks/sale/useMultiplePayment';
import { useSale } from '../../../../hooks/sale/useSale';
import { useCurrencyInput } from '../../../../hooks/useCurrencyInput';

interface Props {
  editingPayment: any | null;
  onDoneEditing: () => void;
}

const PaymentMethodInputs: React.FC<Props> = ({
  editingPayment,
  onDoneEditing,
}) => {
  const { createPayment, updatePaymentById, paymentMethods } =
    useMultiplePayment();
  const { form } = useSale();

  const saleId = form.id;

  const isEditing = editingPayment !== null;

  const [paymentMethodCode, setPaymentMethodCode] = useState('');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');

  const [paymentMethodError, setPaymentMethodError] = useState('');
  const [amountError, setAmountError] = useState('');
  const [referenceError, setReferenceError] = useState('');

  const isVoucherRequired = paymentMethodCode !== '01';

  // 💵 Monto — formato legible (6789 -> 6,789.00)
  const amountInput = useCurrencyInput(amount, (value, isEmpty) => {
    setAmount(isEmpty ? '' : String(value));
  });

  // 🔄 Precargar el formulario cuando se entra en modo edición
  useEffect(() => {
    if (!editingPayment) {
      setPaymentMethodCode('');
      setAmount('');
      setReference('');
      return;
    }

    const method = paymentMethods.find(
      (m) => m.id === editingPayment.paymentMethodId
    );

    setPaymentMethodCode(method?.code ?? '');
    setAmount(String(editingPayment.amount ?? ''));
    setReference(editingPayment.reference ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPayment]);

  const handleCancelEdit = () => {
    onDoneEditing();
  };

  const handleSubmit = async () => {
    setPaymentMethodError('');
    setAmountError('');
    setReferenceError('');

    let hasError = false;

    if (!saleId) return;

    if (!paymentMethodCode) {
      setPaymentMethodError('Seleccione una forma de pago.');
      hasError = true;
    }

    if (!amount || Number(amount) <= 0) {
      setAmountError('Ingrese un monto válido mayor a 0.');
      hasError = true;
    }

    if (isVoucherRequired && !reference.trim()) {
      setReferenceError('Este campo es obligatorio para esta forma de pago.');
      hasError = true;
    }

    if (hasError) return;

    const dto: any = {
      paymentMethodCode,
      amount: Number(amount),
      changeAmount: 0,
    };

    // 👉 Solo agregar referencia si existe
    const normalizedReference = reference?.trim();

    if (normalizedReference) {
      dto.reference = normalizedReference;
    }

    // 👉 Solo efectivo
    if (paymentMethodCode === '01') {
      dto.cashReceived = Number(amount);
    }

    const success = isEditing
      ? await updatePaymentById(saleId, editingPayment.id, dto)
      : await createPayment(saleId, dto);

    if (success) {
      setAmount('');
      setReference('');

      if (isEditing) {
        onDoneEditing();
      }
    }
  };

  return (
    <div className="flex w-full flex-col gap-5">
      {isEditing && (
        <div className="flex items-center justify-between rounded-md bg-primary/10 px-3 py-2 text-xs font-medium text-primary">
          Editando pago
          <button
            type="button"
            onClick={handleCancelEdit}
            className="font-semibold underline"
          >
            Cancelar
          </button>
        </div>
      )}

      {/* 🔹 Forma de pago */}
      <SelectField
        label="Forma de Pago"
        name="paymentMethod"
        iconKey="payment"
        value={paymentMethodCode}
        onChange={(e) => setPaymentMethodCode(String(e.target.value))}
        options={paymentMethods.map((method) => ({
          label: method.name,
          value: method.code,
        }))}
        error={paymentMethodError}
        required
      />

      {/* 🔹 Monto */}
      <InputField
        label="Monto"
        name="totalAmount"
        iconKey="dollar"
        type="text"
        inputMode="decimal"
        placeholder="$0.00"
        value={amountInput.displayValue}
        onFocus={amountInput.onFocus}
        onBlur={amountInput.onBlur}
        onKeyDown={amountInput.onKeyDown}
        onChange={(e) => amountInput.onChangeText(e.target.value)}
        error={amountError}
        required
      />

      {/* 🔹 Referencia */}
      <InputField
        label="Comprobante / Transferencia"
        name="voucherCode"
        iconKey="file"
        type="text"
        placeholder="Número de comprobante/transferencia"
        value={reference}
        onChange={(e) => setReference(e.target.value)}
        disabled={!isVoucherRequired}
        error={referenceError}
        required={isVoucherRequired}
      />

      {/* 🔹 Acción */}
      <div className="flex justify-end gap-2 pt-2">
        {isEditing && (
          <button
            type="button"
            onClick={handleCancelEdit}
            className="rounded-md border border-stroke px-5 py-2 text-sm font-medium text-black hover:bg-gray-100 dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
          >
            Cancelar
          </button>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-md bg-primary px-5 py-2 text-sm font-medium text-white hover:bg-opacity-90"
        >
          {isEditing ? 'Guardar Cambios' : '+ Agregar Pago'}
        </button>
      </div>
    </div>
  );
};

export default PaymentMethodInputs;
